import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Stack,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { FeeCard, FeeCardProps } from '../../../FeeStructure/FeeCard';
import { useSelector } from 'react-redux';
import { globalConfigurationSelector } from '../../../../selectors/selectors';

interface IRawFeeStructure {
    name?: string;
    description?: string;
    amount?: number;
    _id: string;
    fee: number;
    currency: string;
    fee_features: string[];
    fee_name: string;
    discount_message: string;
    original_price: number;
}

interface EventFeesTabProps {
    fees: IRawFeeStructure[];
    onAddFee: (fee: Omit<IRawFeeStructure, '_id'>) => void;
    onEditFee: (fee: IRawFeeStructure) => void;
    onDeleteFee: (feeId: string) => void;
}

export const EventFeesTab: React.FC<EventFeesTabProps> = ({ fees = [], onAddFee, onEditFee, onDeleteFee }) => {
    const { themeStyle } = useThemeContext();
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [editingFee, setEditingFee] = useState<IRawFeeStructure | null>(null);
    const globalConfig = useSelector(globalConfigurationSelector);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [newFeature, setNewFeature] = useState('');

    const [newFee, setNewFee] = useState<Partial<IRawFeeStructure>>({
        name: '',
        description: '',
        amount: 0,
        fee: 0,
        currency: (globalConfig?.default_currency || 'USD') as 'USD' | 'NGN' | 'EUR' | 'GBP',
        fee_features: [],
        fee_name: '',
    });

    useEffect(() => {
        if (globalConfig?.default_currency) {
            setNewFee(prev => ({
                ...prev,
                currency: globalConfig.default_currency as 'USD' | 'NGN' | 'EUR' | 'GBP',
                transaction_currency: globalConfig.default_currency as 'USD' | 'NGN' | 'EUR' | 'GBP',
                original_currency: globalConfig.default_currency as 'USD' | 'NGN' | 'EUR' | 'GBP',
            }));
        }
    }, [globalConfig?.default_currency]);

    const calculateFees = (amount: number) => {
        const amountInCents = amount * 100;
        const salesFees = globalConfig?.sales_fees;
        const platformFixedFee = salesFees?.platform_fixed_fee || 0;
        const platformFeePercentage = salesFees?.platform_fee || 0;
        const stripeFixedFee = salesFees?.stripe_fixed_fee || 0;
        const stripeFeePercentage = salesFees?.stripe_fees || 0;

        const platformPercentageFee = Math.round((amountInCents * platformFeePercentage) / 100);
        const stripePercentageFee = Math.round((amountInCents * stripeFeePercentage) / 100);
        const totalFees = platformFixedFee + platformPercentageFee + stripeFixedFee + stripePercentageFee;
        const userWillPay = amountInCents + totalFees;
        const hostWillReceive = amountInCents;

        return {
            amountInCents,
            platformFixedFee,
            platformPercentageFee,
            stripeFixedFee,
            stripePercentageFee,
            totalFees,
            userWillPay,
            hostWillReceive,
            platformFeePercentage: platformFeePercentage,
            stripeFeePercentage: stripeFeePercentage,
        };
    };

    const validateFee = () => {
        const newErrors: Record<string, string> = {};

        if (!newFee.name) {
            newErrors.name = 'Fee name is required';
        }

        const amountInCents = (newFee.amount || 0) * 100;
        if (!editingFee && amountInCents < (globalConfig?.min_ticket_price || 0)) {
            newErrors.amount = `Minimum amount is ${(globalConfig?.min_ticket_price || 0) / 100} ${newFee.currency}`;
        }

        if (amountInCents > (globalConfig?.max_ticket_price || 1000000)) {
            newErrors.amount = `Maximum amount is ${(globalConfig?.max_ticket_price || 1000000) / 100} ${
                newFee.currency
            }`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isFormValid = () => {
        // Check if name exists
        if (!newFee.name?.trim()) return false;

        // Check if there's at least one feature
        if (!newFee.fee_features?.length) return false;

        // Check if amount is at least $3
        const amountInDollars = newFee.amount || 0;
        if (amountInDollars < 3) return false;

        return true;
    };

    const handleOpenAddDialog = () => {
        setNewFee({
            name: '',
            description: '',
            amount: 0,
            fee: 0,
            currency: (globalConfig?.default_currency || 'USD') as 'USD' | 'NGN' | 'EUR' | 'GBP',
            fee_features: [],
        });
        setErrors({});
        setOpenAddDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenAddDialog(false);
        setEditingFee(null);
        setErrors({});
        setNewFeature('');
    };

    const handleSubmitFee = () => {
        if (!validateFee()) {
            return;
        }

        const amountInCents = (newFee.amount || 0) * 100;

        if (editingFee) {
            onEditFee({
                ...editingFee,
                ...newFee,
                fee_name: newFee.name || '',
                fee: amountInCents,
                fee_features: newFee.fee_features || [],
            } as IRawFeeStructure);
        } else {
            onAddFee({
                ...newFee,
                fee_name: newFee.name || '',
                fee: amountInCents,
                fee_features: newFee.fee_features || [],
            } as Omit<IRawFeeStructure, '_id'>);
        }
        handleCloseDialog();
    };

    const handleEditFee = (fee: IRawFeeStructure) => {
        setEditingFee(fee);
        setErrors({});

        setNewFee({
            ...fee,
            name: fee.fee_name || '',
            description: fee.fee_features && fee.fee_features.length > 0 ? fee.fee_features[0] : '',
            amount: fee.fee / 100,
            fee_features: fee.fee_features || [],
        });

        setOpenAddDialog(true);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const { name, value } = e.target;
        setNewFee(prev => {
            if (name === 'fee_name') {
                return {
                    ...prev,
                    name: value as string,
                    fee_name: value as string,
                };
            }

            if (name === 'currency') {
                const currencyValue = value as 'USD' | 'NGN' | 'EUR' | 'GBP';
                return {
                    ...prev,
                    currency: currencyValue,
                    transaction_currency: currencyValue,
                    original_currency: currencyValue,
                };
            }

            return {
                ...prev,
                [name as string]: value,
            };
        });
    };

    const handleAddFeature = () => {
        if (newFeature.trim()) {
            setNewFee(prev => ({
                ...prev,
                fee_features: [...(prev.fee_features || []), newFeature.trim()],
            }));
            setNewFeature('');
        }
    };

    const handleRemoveFeature = (index: number) => {
        setNewFee(prev => ({
            ...prev,
            fee_features: prev.fee_features?.filter((_, i) => i !== index) || [],
        }));
    };

    const handleFeeAction = (action: string, fee: IRawFeeStructure) => {
        switch (action) {
            case 'edit':
                handleEditFee(fee);
                break;
            case 'delete':
                onDeleteFee(fee._id);
                break;
            default:
                break;
        }
    };

    const feeActions = [
        { id: 'edit', title: 'Edit Fee' },
        { id: 'delete', title: 'Delete Fee' },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '1.25rem',
                        color: theme[themeStyle].primary_white,
                    }}>
                    Fee Structure
                </Typography>
                <Button
                    startIcon={<AddIcon />}
                    onClick={handleOpenAddDialog}
                    sx={{
                        backgroundColor: theme[themeStyle].highlight,
                        color: theme[themeStyle].black,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].primary_orange,
                            opacity: 0.9,
                        },
                        px: 2,
                        py: 1,
                    }}>
                    Add New Fee
                </Button>
            </Box>

            {fees.length === 0 ? (
                <Box
                    sx={{
                        backgroundColor: theme[themeStyle].table_header,
                        borderRadius: 2,
                        padding: 4,
                        textAlign: 'center',
                    }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            fontSize: '1rem',
                            color: theme[themeStyle].primary_white,
                        }}>
                        No fees have been added yet
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {fees.map(fee => (
                        <Grid item xs={12} md={6} lg={4} key={fee._id}>
                            <FeeCard
                                fee={fee as unknown as FeeCardProps['fee']}
                                actions={feeActions}
                                onActionSelect={handleFeeAction}
                                manageMode
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Add/Edit Fee Dialog */}
            <Dialog
                open={openAddDialog}
                onClose={handleCloseDialog}
                PaperProps={{
                    sx: {
                        backgroundColor: theme[themeStyle].pure_black,
                        borderRadius: 2,
                        minWidth: { xs: '90%', sm: '500px' },
                    },
                }}>
                <DialogTitle
                    sx={{
                        color: theme[themeStyle].primary_white,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        borderBottom: `1px solid ${theme[themeStyle].placeholderGray}`,
                        pb: 2,
                    }}>
                    {editingFee ? 'Edit Fee' : 'Add New Fee'}
                </DialogTitle>
                <DialogContent
                    sx={{
                        pt: 3,
                        '&.MuiDialogContent-root': {
                            paddingTop: '16px',
                        },
                    }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                name="fee_name"
                                label="Fee Name"
                                value={newFee.name}
                                onChange={handleInputChange}
                                fullWidth
                                required
                                error={!!errors.name}
                                helperText={errors.name}
                                InputLabelProps={{
                                    sx: { color: theme[themeStyle].light_gray_text },
                                }}
                                InputProps={{
                                    sx: {
                                        color: theme[themeStyle].primary_white,
                                        backgroundColor: theme[themeStyle].table_header,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="amount"
                                label="Amount"
                                type="number"
                                value={newFee.amount}
                                onChange={handleInputChange}
                                fullWidth
                                required
                                error={!!errors.amount}
                                helperText={errors.amount}
                                InputLabelProps={{
                                    sx: { color: theme[themeStyle].light_gray_text },
                                }}
                                InputProps={{
                                    sx: {
                                        color: theme[themeStyle].primary_white,
                                        backgroundColor: theme[themeStyle].table_header,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: theme[themeStyle].light_gray_text }}>Currency</InputLabel>
                                <Select
                                    name="currency"
                                    value={newFee.currency}
                                    onChange={e => handleInputChange(e as any)}
                                    label="Currency"
                                    sx={{
                                        color: theme[themeStyle].primary_white,
                                        backgroundColor: theme[themeStyle].table_header,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: theme[themeStyle].primary_white,
                                        },
                                    }}>
                                    {(globalConfig?.currency_options || ['USD', 'NGN', 'EUR', 'GBP']).map(currency => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].grey,
                                        mb: 1,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    }}>
                                    Fee Features
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <TextField
                                        value={newFeature}
                                        onChange={e => setNewFeature(e.target.value)}
                                        placeholder="Add a feature"
                                        fullWidth
                                        onKeyPress={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddFeature();
                                            }
                                        }}
                                        InputProps={{
                                            sx: {
                                                color: theme[themeStyle].tag_white,
                                                backgroundColor: theme[themeStyle].table_header,
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor:
                                                        themeStyle === 'dark'
                                                            ? theme[themeStyle].placeholderGray50
                                                            : theme[themeStyle].highlight,
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor:
                                                        themeStyle === 'dark'
                                                            ? theme[themeStyle].placeholderGray50
                                                            : theme[themeStyle].highlight,
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor:
                                                        themeStyle === 'dark'
                                                            ? theme[themeStyle].placeholderGray50
                                                            : theme[themeStyle].highlight,
                                                },
                                            },
                                        }}
                                    />
                                    <Button
                                        onClick={handleAddFeature}
                                        sx={{
                                            backgroundColor: theme[themeStyle].highlight,
                                            color: theme[themeStyle].black,
                                            '&:hover': {
                                                backgroundColor: theme[themeStyle].primary_orange,
                                                opacity: 0.9,
                                            },
                                        }}>
                                        Add
                                    </Button>
                                </Box>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {newFee.fee_features?.map((feature, index) => (
                                        <Chip
                                            key={index}
                                            label={feature}
                                            onDelete={() => handleRemoveFeature(index)}
                                            sx={{
                                                backgroundColor: theme[themeStyle].table_header,
                                                color: theme[themeStyle].tag_white,
                                                margin: '4px',
                                                '& .MuiChip-deleteIcon': {
                                                    color: theme[themeStyle].tag_white,
                                                    '&:hover': {
                                                        color: theme[themeStyle].tag_white,
                                                    },
                                                },
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        </Grid>

                        {/* Fee Calculation Display */}
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    backgroundColor: theme[themeStyle].table_header,
                                    borderRadius: 2,
                                    p: 2,
                                    mt: 2,
                                }}>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].primary_white,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        mb: 2,
                                    }}>
                                    Fee Breakdown
                                </Typography>
                                {newFee.amount && newFee.amount > 0 ? (
                                    <>
                                        {(() => {
                                            const fees = calculateFees(newFee.amount);
                                            return (
                                                <>
                                                    <Typography
                                                        sx={{
                                                            color: theme[themeStyle].light_gray_text,
                                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                            fontSize: '0.9rem',
                                                            mb: 1,
                                                        }}>
                                                        • Ticket Price: {newFee.amount} {newFee.currency}
                                                    </Typography>
                                                    {fees.platformFixedFee > 0 && (
                                                        <Typography
                                                            sx={{
                                                                color: theme[themeStyle].light_gray_text,
                                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                                fontSize: '0.9rem',
                                                                mb: 1,
                                                            }}>
                                                            • Platform Fixed Fee: {fees.platformFixedFee / 100}{' '}
                                                            {newFee.currency}
                                                        </Typography>
                                                    )}
                                                    {fees.platformFeePercentage > 0 && (
                                                        <Typography
                                                            sx={{
                                                                color: theme[themeStyle].light_gray_text,
                                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                                fontSize: '0.9rem',
                                                                mb: 1,
                                                            }}>
                                                            • Platform Percentage Fee ({fees.platformFeePercentage}%):{' '}
                                                            {fees.platformPercentageFee / 100} {newFee.currency}
                                                        </Typography>
                                                    )}
                                                    {fees.stripeFixedFee > 0 && (
                                                        <Typography
                                                            sx={{
                                                                color: theme[themeStyle].light_gray_text,
                                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                                fontSize: '0.9rem',
                                                                mb: 1,
                                                            }}>
                                                            • Stripe Fixed Fee: {(fees.stripeFixedFee / 100).toFixed(2)}{' '}
                                                            {newFee.currency}
                                                        </Typography>
                                                    )}
                                                    {fees.stripeFeePercentage > 0 && (
                                                        <Typography
                                                            sx={{
                                                                color: theme[themeStyle].light_gray_text,
                                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                                fontSize: '0.9rem',
                                                                mb: 1,
                                                            }}>
                                                            • Stripe Percentage Fee ({fees.stripeFeePercentage}%):{' '}
                                                            {(fees.stripePercentageFee / 100).toFixed(2)}{' '}
                                                            {newFee.currency}
                                                        </Typography>
                                                    )}
                                                    <Typography
                                                        sx={{
                                                            color: theme[themeStyle].primary_white,
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            fontSize: '0.9rem',
                                                            mt: 2,
                                                            mb: 1,
                                                        }}>
                                                        User will pay: {(fees.userWillPay / 100).toFixed(2)}{' '}
                                                        {newFee.currency}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            color: theme[themeStyle].highlight,
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            fontSize: '0.9rem',
                                                        }}>
                                                        You will receive: {fees.hostWillReceive / 100} {newFee.currency}
                                                    </Typography>
                                                </>
                                            );
                                        })()}
                                    </>
                                ) : (
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            fontSize: '0.9rem',
                                        }}>
                                        Enter an amount to see fee breakdown
                                    </Typography>
                                )}

                                {/* Fee Information */}
                                <Box sx={{ mt: 3 }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            mb: 1,
                                        }}>
                                        Important Information
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            fontSize: '0.9rem',
                                            mb: 1,
                                        }}>
                                        • Minimum ticket price: {(globalConfig?.min_ticket_price || 0) / 100}{' '}
                                        {newFee.currency}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            fontSize: '0.9rem',
                                            mb: 1,
                                        }}>
                                        • Platform charges a flat fee of{' '}
                                        {(globalConfig?.sales_fees?.platform_fixed_fee || 0) / 100} {newFee.currency}
                                        {globalConfig?.sales_fees?.platform_fee
                                            ? ` plus ${globalConfig.sales_fees.platform_fee}% per ticket`
                                            : ''}
                                        {globalConfig?.sales_fees?.stripe_fixed_fee
                                            ? ` plus ${((globalConfig.sales_fees.stripe_fixed_fee || 0) / 100).toFixed(
                                                  2,
                                              )} ${newFee.currency} in Stripe fixed fees`
                                            : ''}
                                        {globalConfig?.sales_fees?.stripe_fees
                                            ? ` plus ${globalConfig.sales_fees.stripe_fees}% in Stripe percentage fees`
                                            : ''}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            fontSize: '0.9rem',
                                        }}>
                                        • Note: End users will pay in their local currency. While you receive payment in{' '}
                                        {newFee.currency}, please account for potential exchange rate fluctuations and
                                        bank conversion fees.
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme[themeStyle].placeholderGray}` }}>
                    <Button
                        onClick={handleCloseDialog}
                        sx={{
                            color: theme[themeStyle].primary_white,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitFee}
                        disabled={!isFormValid()}
                        sx={{
                            backgroundColor: theme[themeStyle].highlight,
                            color: theme[themeStyle].black,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].primary_orange,
                                opacity: 0.9,
                            },
                            '&.Mui-disabled': {
                                backgroundColor: theme[themeStyle].placeholderGray,
                                color: theme[themeStyle].light_gray_text,
                            },
                            px: 2,
                        }}>
                        {editingFee ? 'Update Fee' : 'Add Fee'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
